<template>
  <gs-alert-bar
    class="mt-4"
    type="warning"
    :title="t('limit_hit_warning_title')"
    :message="t('limit_hit_warning_description')"
  />
  <div class="select-custom-spending-limit">
    <h3>{{ t('spending_limit_title') }}</h3>
    <p class="lead">{{ t('spending_limit_description') }}</p>
    <div class="selector">
      <div
        v-for="(limit, type) in limitTypes"
        :key="type"
        class="selector-option"
        :class="{ selected: selectedLimitType === type }"
        @click="selectLimit(type)"
      >
        <span class="selector-option-title">
          {{ t(type) }}
          <BaseTooltip :content="t(`tooltip.${type}`)" color="#0017B4" />
        </span>
        <span class="selector-option-value">$ {{ formatNumberByLocale(limit) }} US Dollars</span>
      </div>
      <div
        class="selector-option"
        :class="{ selected: selectedLimitType === 'custom' }"
        @click="selectLimit('custom')"
      >
        <span class="selector-option-title">{{ t('custom') }}</span>
      </div>
    </div>
    <v-form v-if="selectedLimitType === 'custom'" v-model="isSpendingLimitFormValid">
      <v-text-field
        v-model.number="selectedSpendingLimit"
        :label="t('monthly_limit')"
        variant="outlined"
        :rules="[
          rules.positiveInteger,
          rules.minValue(
            minLimit,
            t('min_spending_limit', {
              min: formatNumberByLocale(minLimit),
              amount: formatNumberByLocale(selectedStore.spendingLimit?.used || 0),
              plan: getPlanName(selectedPlan || currentPlanType),
            }),
          ),
          rules.required,
        ]"
        autofocus
        :hide-details="false"
      >
        <template v-slot:prepend-inner>
          <strong>$</strong>
        </template>
      </v-text-field>
    </v-form>
    <div v-if="currentSpendingLimit" class="change-custom-spending-limit">
      <span>
        {{ t('current_spending_limit') }} ${{ formatNumberByLocale(currentSpendingLimit) }}
      </span>
      <v-icon :size="24">mdi-arrow-right</v-icon>
      <span>
        {{ t('selected_spending_limit') }} ${{ formatNumberByLocale(selectedSpendingLimit) }}
      </span>
    </div>
    <div class="button-wrapper">
      <gs-button
        @click="$emit('back')"
        icon="mdi-chevron-left"
        size="large"
        type="outlined"
        :disabled="loading"
      >
        {{ $t('CommonUi.back') }}
      </gs-button>
      <gs-button
        @click="handleSave"
        :loading="loading"
        type="primary"
        size="large"
        full-width
        :disabled="(selectedLimitType === 'custom' && !isSpendingLimitFormValid) || loading"
      >
        {{ $t('CommonUi.save') }}
      </gs-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { type Integration } from '@/store/integrations'
import { RulesMixin } from '@/helpers/mixins/RulesMixin.vue'
import { IntegrationsMixin } from '@/helpers/mixins/integrationsMixin'
import { Utils } from '@/helpers/mixins/utilsMixin'
import { setIntegrationSpendingLimit } from '@api/index'
import type { Notification } from '@/store/notification'

import BaseTooltip from '@/components/tools/BaseTooltip.vue'
import type { SubscriptionItem } from '@/store/subscriptions'
import type {
  NewPaidBusinessSubscriptionType,
  PersonalSubscriptionType,
  TechnicalAndBusinessSubscriptionType,
} from '@/helpers/pricing'

type SpendingLimitType = 'recommended' | 'enhanced' | 'custom'

export default defineComponent({
  name: 'SelectCustomSpendingLimit',
  components: {
    BaseTooltip,
  },
  mixins: [RulesMixin, IntegrationsMixin, Utils],
  emits: ['back', 'save'],
  data() {
    return {
      currentSpendingLimit: 0,
      selectedSpendingLimit: 0,
      selectedLimitType: 'recommended',
      isSpendingLimitFormValid: false,
      loading: false,
    } as {
      currentSpendingLimit: number
      selectedSpendingLimit: number
      selectedLimitType: SpendingLimitType
      isSpendingLimitFormValid: boolean
      loading: boolean
    }
  },
  async created() {
    const spendingLimit = this.selectedStore?.spendingLimit?.activeLimit

    this.currentSpendingLimit = spendingLimit ?? 0
    this.selectedSpendingLimit = this.limitTypes[this.selectedLimitType]

    if (this.currentSpendingLimit) {
      if (this.currentSpendingLimit === this.limitTypes.recommended) {
        this.selectedLimitType = 'recommended'
      } else if (this.currentSpendingLimit === this.limitTypes.enhanced) {
        this.selectedLimitType = 'enhanced'
      } else {
        this.selectedLimitType = 'custom'
        this.selectedSpendingLimit =
          this.selectedStore?.spendingLimit?.pendingLimit || spendingLimit || 0
      }
    }
  },
  computed: {
    activePaidSubscriptionItem(): SubscriptionItem {
      return this.$store.getters['getActivePaidSubscriptionItem']
    },
    ownFreeSubscriptionItem(): SubscriptionItem {
      return this.$store.getters['getOwnFreeSubscriptionItem']
    },
    currentPlanType(): TechnicalAndBusinessSubscriptionType {
      if (this.ownFreeSubscriptionItem) {
        return this.ownFreeSubscriptionItem.product as TechnicalAndBusinessSubscriptionType
      }

      return this.activePaidSubscriptionItem.product as TechnicalAndBusinessSubscriptionType
    },
  },
  methods: {
    t(key: string, params?: { [k: string]: string | number }) {
      return this.$t(`ShopifyCustomSpendingLimitView.${key}`, params || {})
    },
    selectLimit(limitType: SpendingLimitType) {
      this.selectedLimitType = limitType

      if (limitType === 'custom' && this.currentSpendingLimit) {
        this.selectedSpendingLimit = this.currentSpendingLimit
      } else {
        this.selectedSpendingLimit = this.limitTypes[limitType] ?? 0
      }
    },
    async handleSave() {
      const successUrl =
        this.successUrl ||
        `${window.location.origin}/shopify/success?store=${this.selectedStore.id}`

      this.loading = true

      try {
        await setIntegrationSpendingLimit(this.selectedStore.id, {
          limit: this.selectedSpendingLimit,
          returnUrl: successUrl,
          product: this.selectedPlan,
        })
        await this.setIntegrations()
        this.$emit('save', this.selectedSpendingLimit)
      } catch (error) {
        let errorMessage

        const SPENDING_LIMIT_TOO_LOW = 'SPENDING_LIMIT_TOO_LOW'

        switch (error.response?.data.message) {
          case SPENDING_LIMIT_TOO_LOW:
            errorMessage = this.t('min_spending_limit', {
              min: this.formatNumberByLocale(this.minLimit),
              amount: this.formatNumberByLocale(this.selectedStore.spendingLimit?.used || 0),
              plan: this.getPlanName(this.selectedPlan || this.currentPlanType),
            })
            break
          default:
            errorMessage = this.t('error')
        }

        this.$store.dispatch('notification/notify', {
          text: errorMessage,
          isError: true,
        } as Notification)
      } finally {
        this.loading = false
      }
    },
  },
  props: {
    selectedStore: {
      type: Object as PropType<Integration>,
      required: true,
    },
    selectedPlan: {
      type: String as PropType<NewPaidBusinessSubscriptionType | PersonalSubscriptionType>,
    },
    successUrl: {
      type: String,
    },
    limitTypes: {
      type: Object as PropType<Record<'recommended' | 'enhanced', number>>,
      required: true,
      default: () => ({
        recommended: 0,
        enhanced: 0,
      }),
    },
    minLimit: {
      type: Number,
      required: true,
      default: 0,
    },
  },
})
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

.select-custom-spending-limit {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid var(--gray-light-CC);
}

.gs-alert-bar {
  :deep(a) {
    color: var(--ui-blue);
    text-decoration: underline;
    cursor: pointer;
  }
}

h3 {
  font-size: 24px;
  margin-bottom: 8px;
}

.lead {
  font-size: 18px;
  margin-bottom: 0;
}

.selector {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 24px 0;
}

.selector-option {
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 100%;
  min-height: 105px;
  gap: 8px;
  flex: 1 0 0;
  border-radius: var(--border-radius-big);
  border: 1px solid var(--gray-light-CC);
  cursor: pointer;

  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    min-width: auto;
  }

  &.selected {
    color: var(--ui-green);
    border-color: var(--ui-green);

    :deep(.tooltip-icon) {
      color: var(--ui-green) !important;
    }
  }
}

.selector-option-title {
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;

  :deep(.tooltip) {
    font-size: 20px;
    margin-left: 0;
  }
}

.selector-option-value {
  font-size: 24px;
  font-weight: bold;
}

.change-custom-spending-limit {
  display: flex;
  align-items: center;
  gap: 44px;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid var(--gray-light-CC);

  span {
    font-size: 20px;
    font-weight: bold;
  }
}

.button-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 32px;
}
</style>
